import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Container, AppBar, Toolbar, TextField,
  InputAdornment, Chip, CircularProgress, Paper, Stack, IconButton, useMediaQuery,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider,
} from '@mui/material';
import { Search, Login, Refresh, ArrowUpward, DirectionsCar, Close } from '@mui/icons-material';
import { productsAPI } from '../services/api';
import logo from './logo.png';

// ---- Theme tokens: dark trading-terminal, green + orange ----
const C = {
  bg: '#0A130F',
  panel: '#0F1D17',
  panelAlt: '#0C1712',
  border: 'rgba(255,255,255,0.08)',
  borderStrong: 'rgba(255,255,255,0.14)',
  orange: '#FF8A3D',
  orangeDim: 'rgba(255,138,61,0.14)',
  green: '#33D17A',
  greenDim: 'rgba(51,209,122,0.14)',
  text: '#F2F5F3',
  textMuted: 'rgba(242,245,243,0.55)',
  textFaint: 'rgba(242,245,243,0.35)',
};

const MONO = "'Roboto Mono', 'SFMono-Regular', Menlo, Consolas, monospace";

const fmtPrice = (value, currency = 'USD') => {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: currency || 'USD', maximumFractionDigits: 0,
    }).format(Number(value) || 0);
  } catch {
    return `${currency || 'USD'} ${Number(value) || 0}`;
  }
};

/**
 * Defensively resolve warehouse info from whatever shape the API sends.
 * Prod likely nests/names this differently than dev — log a raw product
 * from the prod response (see commented console.log below) to confirm
 * the real shape, then trim this down to just that case.
 */
const getWarehouseLabel = (product) => {
  const raw =
    product.warehouse_stock ??
    product.warehouseStock ??
    product.warehouses ??
    product.warehouse_name ??
    product.stock_by_warehouse ??
    product.warehouseStockSummary;

  if (raw === undefined || raw === null || raw === '') return '—';

  if (Array.isArray(raw)) {
    const parts = raw
      .map((w) => {
        if (typeof w === 'string') return w;
        const name = w.warehouse_name || w.name || w.warehouse || w.label;
        const qty = w.quantity ?? w.stock ?? w.qty;
        if (!name) return null;
        return qty !== undefined ? `${name} (${qty})` : name;
      })
      .filter(Boolean);
    return parts.length ? parts.join(', ') : '—';
  }

  if (typeof raw === 'object') {
    const parts = Object.entries(raw).map(([name, qty]) => `${name} (${qty})`);
    return parts.length ? parts.join(', ') : '—';
  }

  return String(raw);
};

const PortfolioPage = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [detailLoading, setDetailLoading] = useState(false);
  const isMobile = useMediaQuery('(max-width:639px)');

  const openProduct = async (product) => {
    setDetailLoading(true);
    setSelectedImage(0);
    try {
      const response = await productsAPI.getById(product.id);
      setSelectedProduct(response.data.data);
    } catch {
      setSelectedProduct(product);
    } finally {
      setDetailLoading(false);
    }
  };

  const loadProducts = () => {
    setLoading(true);
    productsAPI.getAll({
      page: 1, limit: 100, search: search || undefined,
      status: 'available', is_published: true, sort: 'created_at', order: 'DESC',
      // If prod needs an explicit join to include warehouse stock:
      // include: 'warehouseStock',
    })
      .then((response) => {
        const data = response.data.data || [];
        // console.log('sample product from API:', data[0]);
        setProducts(data);
        setLastUpdated(new Date());
      })
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const timer = setTimeout(loadProducts, 250);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const marketRows = useMemo(() => products.map((product) => ({
    ...product,
    displayName: `${product.brand_name ? `${product.brand_name} ` : ''}${product.name || 'Vehicle'}`,
    detail: [product.year, product.model].filter(Boolean).join(' · ') || 'Vehicle listing',
    price: fmtPrice(product.selling_price, product.currency),
    stock: Number(product.total_stock) || 0,
    warehouseLabel: getWarehouseLabel(product),
  })), [products]);

  const tickerItems = marketRows.length
    ? [...marketRows, ...marketRows] // duplicate for seamless scroll loop
    : [];
  const detailImages = (selectedProduct?.images || []).filter((image) => image?.url);
  const detailImage = detailImages[selectedImage]?.url || selectedProduct?.primary_image;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: C.bg, color: C.text }}>
      <style>{`
        @keyframes ticker-scroll {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
        .ddig-exchange-table thead th,
        .ddig-exchange-table thead td {
          background-color: ${C.panel} !important;
          color: ${C.textFaint} !important;
        }
      `}</style>

      <AppBar position="sticky" elevation={0} sx={{ bgcolor: C.bg, borderBottom: `1px solid ${C.border}` }}>
        <Toolbar sx={{ px: { xs: 1.5, sm: 4 }, minHeight: { xs: 56, sm: 72 }, position: 'relative' }}>
          {/* Login button pinned to the right */}
          <IconButton
            onClick={() => navigate('/login')}
            sx={{
              color: C.green, border: `1px solid ${C.greenDim}`, borderRadius: 1.5,
              p: { xs: 1, sm: '6px 14px' }, ml: 'auto',
            }}
            aria-label="Staff login"
          >
            <Login fontSize="small" />
            <Typography component="span" sx={{ display: { xs: 'none', sm: 'inline' }, ml: 0.75, fontSize: '0.82rem', fontWeight: 700 }}>
              Staff Login
            </Typography>
          </IconButton>

          {/* Logo, centered regardless of the login button's width */}
          <Box sx={{
            position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)',
            display: 'flex', alignItems: 'center', gap: 1,
          }}>
            <Box
              component="img"
              src={logo}
              alt="DDIG TRADING PLC"
              sx={{ height: { xs: 26, sm: 34 }, width: 'auto', display: 'block' }}
              onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
            />
            {/* Fallback shown only if logo.png fails to load */}
            <Box sx={{
              display: 'none', width: 30, height: 30, borderRadius: 1, alignItems: 'center', justifyContent: 'center',
              background: `linear-gradient(135deg, ${C.orange}, ${C.green})`,
            }}>
              <DirectionsCar sx={{ color: '#0A130F', fontSize: 18 }} />
            </Box>
            <Typography noWrap sx={{ fontWeight: 800, fontSize: { xs: '0.9rem', sm: '1.15rem' } }}>
              DDIG TRADING PLC
            </Typography>
          </Box>
        </Toolbar>

        {/* Ticker tape */}
        {tickerItems.length > 0 && (
          <Box sx={{
            bgcolor: C.panelAlt, borderBottom: `1px solid ${C.border}`, overflow: 'hidden',
            py: 0.6, display: 'flex', whiteSpace: 'nowrap',
          }}>
            <Box sx={{
              display: 'inline-flex', animation: 'ticker-scroll 38s linear infinite',
              '&:hover': { animationPlayState: 'paused' },
            }}>
              {tickerItems.map((row, i) => (
                <Stack key={`${row.id}-${i}`} direction="row" spacing={0.75} alignItems="center" sx={{ px: 2.5 }}>
                  <Typography sx={{ fontFamily: MONO, fontSize: '0.72rem', color: C.textMuted, fontWeight: 600 }}>
                    {row.displayName.toUpperCase()}
                  </Typography>
                  <ArrowUpward sx={{ fontSize: 11, color: C.green }} />
                  <Typography sx={{ fontFamily: MONO, fontSize: '0.72rem', color: C.orange, fontWeight: 700 }}>
                    {row.price}
                  </Typography>
                </Stack>
              ))}
            </Box>
          </Box>
        )}
      </AppBar>

      <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 5 }, px: { xs: 1, sm: 3 } }}>
        <Box sx={{ mb: { xs: 2, sm: 4 }, px: { xs: 1, sm: 0 } }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: C.green, boxShadow: `0 0 8px ${C.green}` }} />
            <Typography sx={{ color: C.green, fontSize: { xs: '0.65rem', sm: '0.72rem' }, letterSpacing: 2, fontWeight: 700, fontFamily: MONO }}>
              LIVE MARKET · UPDATED {lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </Typography>
          </Stack>
          <Typography sx={{ fontSize: { xs: '1.4rem', sm: '2.5rem' }, lineHeight: 1.15, fontWeight: 800, mb: 0.5 }}>
            Vehicle Pricing List
          </Typography>
          <Typography sx={{ color: C.textMuted, fontSize: { xs: '0.8rem', sm: '0.95rem' } }}>
            Asking prices and live availability — no login required to browse.
          </Typography>
        </Box>

        <Paper elevation={0} sx={{ p: { xs: 1, sm: 2 }, mb: 1.5, mx: { xs: 1, sm: 0 }, bgcolor: C.panel, border: `1px solid ${C.border}`, borderRadius: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              fullWidth
              size="small"
              placeholder="Search make, model or SKU"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: C.textMuted, fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: C.text, bgcolor: 'rgba(0,0,0,0.2)', minHeight: 42, borderRadius: 1.5,
                  '& fieldset': { borderColor: C.border },
                  '&:hover fieldset': { borderColor: C.orange },
                  '&.Mui-focused fieldset': { borderColor: C.orange },
                },
                '& input::placeholder': { color: C.textFaint, opacity: 1, fontSize: '0.85rem' },
              }}
            />
            <IconButton
              onClick={loadProducts}
              sx={{ color: C.orange, border: `1px solid ${C.border}`, borderRadius: 1.5, minWidth: 42, minHeight: 42 }}
              aria-label="Refresh listings"
            >
              <Refresh fontSize="small" />
            </IconButton>
          </Stack>
        </Paper>

        <Paper elevation={0} sx={{
          overflow: 'hidden', bgcolor: C.panel, border: `1px solid ${C.border}`, borderRadius: 2,
          mx: { xs: 1, sm: 0 },
        }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress sx={{ color: C.orange }} />
            </Box>
          ) : marketRows.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8, px: 2 }}>
              <DirectionsCar sx={{ color: C.textFaint, fontSize: 44, mb: 1 }} />
              <Typography sx={{ color: C.textMuted }}>No available vehicles found.</Typography>
            </Box>
          ) : (
            <TableContainer sx={{ maxWidth: '100%', overflowX: isMobile ? 'visible' : 'auto' }}>
              <Table
                size="small"
                className="ddig-exchange-table"
                sx={{
                  borderCollapse: 'separate', tableLayout: 'fixed', width: '100%',
                  minWidth: isMobile ? 0 : 640,
                }}
              >
                <colgroup>
                  {isMobile ? (
                    <>
                      <col style={{ width: '46%' }} />
                      <col style={{ width: '16%' }} />
                      <col style={{ width: '24%' }} />
                      <col style={{ width: '14%' }} />
                    </>
                  ) : (
                    <>
                      <col style={{ width: 220 }} />
                      <col style={{ width: 90 }} />
                      <col style={{ width: 170 }} />
                      <col style={{ width: 110 }} />
                      <col style={{ width: 110 }} />
                    </>
                  )}
                </colgroup>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{
                      position: isMobile ? 'static' : 'sticky', left: 0, zIndex: 2, bgcolor: C.panel,
                      color: C.textFaint, fontFamily: MONO, fontSize: '0.64rem', letterSpacing: 1,
                      fontWeight: 700, borderBottom: `1px solid ${C.borderStrong}`,
                      borderRight: isMobile ? 'none' : `1px solid ${C.border}`, py: 1,
                      px: { xs: 1, sm: 2 },
                    }}>
                      VEHICLE
                    </TableCell>
                    <TableCell align="right" sx={{ bgcolor: C.panel, color: C.textFaint, fontFamily: MONO, fontSize: '0.64rem', letterSpacing: 1, fontWeight: 700, borderBottom: `1px solid ${C.borderStrong}`, px: { xs: 0.75, sm: 2 } }}>
                      {isMobile ? 'QTY' : 'STOCK'}
                    </TableCell>
                    {!isMobile && (
                      <TableCell sx={{ bgcolor: C.panel, color: C.textFaint, fontFamily: MONO, fontSize: '0.64rem', letterSpacing: 1, fontWeight: 700, borderBottom: `1px solid ${C.borderStrong}` }}>
                        WAREHOUSE
                      </TableCell>
                    )}
                    <TableCell align="right" sx={{ bgcolor: C.panel, color: C.textFaint, fontFamily: MONO, fontSize: '0.64rem', letterSpacing: 1, fontWeight: 700, borderBottom: `1px solid ${C.borderStrong}`, px: { xs: 0.75, sm: 2 } }}>
                      PRICE
                    </TableCell>
                    <TableCell align="center" sx={{ bgcolor: C.panel, color: C.textFaint, fontFamily: MONO, fontSize: '0.64rem', letterSpacing: 1, fontWeight: 700, borderBottom: `1px solid ${C.borderStrong}`, px: { xs: 0.5, sm: 2 } }}>
                      {isMobile ? '' : 'STATUS'}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {marketRows.map((row, index) => (
                    <TableRow
                      key={row.id}
                      onClick={() => openProduct(row)}
                      sx={{
                        bgcolor: index % 2 === 0 ? C.panel : C.panelAlt,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'rgba(255,138,61,0.05)' },
                        '&:hover .veh-cell': { bgcolor: 'rgba(255,138,61,0.05)' },
                      }}
                    >
                      <TableCell
                        className="veh-cell"
                        sx={{
                          position: isMobile ? 'static' : 'sticky', left: 0, zIndex: 1,
                          bgcolor: index % 2 === 0 ? C.panel : C.panelAlt,
                          borderBottom: `1px solid ${C.border}`,
                          borderRight: isMobile ? 'none' : `1px solid ${C.border}`,
                          py: 1.1, px: { xs: 1, sm: 2 },
                        }}
                      >
                        <Typography sx={{ color: C.text, fontWeight: 700, fontSize: { xs: '0.78rem', sm: '0.85rem' }, whiteSpace: 'normal', lineHeight: 1.25 }}>
                          {row.displayName}
                        </Typography>
                        <Typography sx={{ color: C.textMuted, fontSize: { xs: '0.66rem', sm: '0.7rem' }, mt: 0.15, whiteSpace: 'normal' }}>
                          {row.detail}
                        </Typography>
                        {isMobile && (
                          <Typography sx={{ color: C.textFaint, fontSize: '0.62rem', mt: 0.15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {row.warehouseLabel}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ borderBottom: `1px solid ${C.border}`, py: 1.1, px: { xs: 0.75, sm: 2 } }}>
                        <Typography sx={{ color: C.green, fontWeight: 700, fontFamily: MONO, fontSize: { xs: '0.76rem', sm: '0.82rem' } }}>
                          {row.stock}
                        </Typography>
                      </TableCell>
                      {!isMobile && (
                        <TableCell sx={{ borderBottom: `1px solid ${C.border}`, py: 1.1 }}>
                          <Typography sx={{ color: C.textMuted, fontSize: '0.76rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {row.warehouseLabel}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell align="right" sx={{ borderBottom: `1px solid ${C.border}`, py: 1.1, px: { xs: 0.75, sm: 2 } }}>
                        <Typography sx={{ color: C.orange, fontWeight: 800, fontFamily: MONO, fontSize: { xs: '0.78rem', sm: '0.9rem' } }}>
                          {row.price}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ borderBottom: `1px solid ${C.border}`, py: 1.1, px: { xs: 0.5, sm: 2 } }}>
                        {isMobile ? (
                          <Box sx={{
                            width: 9, height: 9, borderRadius: '50%', bgcolor: row.stock > 0 ? C.green : C.orange, mx: 'auto',
                            boxShadow: `0 0 6px ${row.stock > 0 ? C.green : C.orange}`,
                          }} aria-label={row.stock > 0 ? 'Available' : 'Sold Out'} />
                        ) : (
                          <Chip
                            label={row.stock > 0 ? 'Available' : 'Sold Out'}
                            size="small"
                            sx={{
                              bgcolor: row.stock > 0 ? C.greenDim : C.orangeDim,
                              color: row.stock > 0 ? C.green : C.orange,
                              fontWeight: 700,
                              fontSize: '0.65rem', height: 21, fontFamily: MONO,
                            }}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          spacing={0.5}
          sx={{ mt: 1.5, px: { xs: 1.5, sm: 0 }, color: C.textFaint, fontSize: '0.7rem' }}
        >
          <Typography variant="caption">Prices are asking prices and subject to confirmation.</Typography>
          <Typography variant="caption" sx={{ display: { xs: 'none', md: 'block' } }}>
            Swipe table to see more →
          </Typography>
        </Stack>
      </Container>

      <Dialog
        open={Boolean(selectedProduct) || detailLoading}
        onClose={() => !detailLoading && setSelectedProduct(null)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { bgcolor: C.panel, color: C.text, border: `1px solid ${C.borderStrong}` } }}
      >
        {detailLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}><CircularProgress sx={{ color: C.orange }} /></Box>
        ) : selectedProduct && (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, color: C.text }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5" fontWeight={800}>
                  {selectedProduct.brand_name ? `${selectedProduct.brand_name} ` : ''}{selectedProduct.name || 'Vehicle'}
                </Typography>
                <Typography variant="body2" sx={{ color: C.textMuted, mt: 0.5 }}>
                  {[selectedProduct.year, selectedProduct.model].filter(Boolean).join(' · ')}
                </Typography>
              </Box>
              <IconButton onClick={() => setSelectedProduct(null)} sx={{ color: C.textMuted }} aria-label="Close details"><Close /></IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ borderColor: C.border }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.15fr 0.85fr' }, gap: 3 }}>
                <Box>
                  <Box sx={{ aspectRatio: '16 / 10', bgcolor: C.panelAlt, borderRadius: 1.5, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {detailImage ? (
                      <Box component="img" src={detailImage} alt={selectedProduct.name || 'Vehicle'} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : <DirectionsCar sx={{ fontSize: 100, color: C.textFaint }} />}
                  </Box>
                  {detailImages.length > 1 && (
                    <Stack direction="row" spacing={1} sx={{ mt: 1, overflowX: 'auto' }}>
                      {detailImages.map((image, index) => (
                        <Box key={image.id || index} component="img" src={image.url} alt="" onClick={() => setSelectedImage(index)} sx={{ width: 72, height: 52, objectFit: 'cover', borderRadius: 1, cursor: 'pointer', border: `2px solid ${selectedImage === index ? C.orange : 'transparent'}` }} />
                      ))}
                    </Stack>
                  )}
                </Box>
                <Box>
                  <Typography sx={{ color: C.orange, fontFamily: MONO, fontSize: '1.35rem', fontWeight: 800, mb: 1.5 }}>
                    {fmtPrice(selectedProduct.selling_price, selectedProduct.currency)}
                  </Typography>
                  <Chip label="Available" size="small" sx={{ bgcolor: C.greenDim, color: C.green, fontWeight: 700, mb: 1.5 }} />
                  <Divider sx={{ borderColor: C.border, mb: 1.5 }} />
                  {[
                    ['Engine', selectedProduct.engine],
                    ['Fuel', selectedProduct.fuel_type], ['Transmission', selectedProduct.transmission],
                    ['Stock', selectedProduct.total_stock],
                  ].filter(([, value]) => value !== null && value !== undefined && value !== '').map(([label, value]) => (
                    <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.55 }}>
                      <Typography sx={{ color: C.textMuted }}>{label}</Typography>
                      <Typography sx={{ color: C.text, fontWeight: 700, textAlign: 'right' }}>{value}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" fontWeight={800} sx={{ mb: 1 }}>Description</Typography>
                <Typography sx={{ color: C.textMuted, lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>
                  {selectedProduct.description || 'No description available.'}
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions sx={{ borderTop: `1px solid ${C.border}`, px: 3, py: 1.5 }}>
              <Button onClick={() => setSelectedProduct(null)} sx={{ color: C.orange }}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default PortfolioPage;
